import {
  ConfirmDialogLabel,
  EmptyData,
  FormLabel,
  InputSearch,
} from "@components";
import {translate} from "@util";
import {
  Box,
  Breakpoint,
  Button,
  Card,
  Grid,
  Skeleton,
  Stack,
  SvgIconOwnProps,
  SxProps,
  Theme,
  Typography,
} from "@mui/material";
import React, {useEffect, useRef, useState} from "react";
import {DeleteDialog, DeleteDialogProps} from "./DeleteDialog";
import {EditDialog, EditDialogProps} from "./EditDialog";
import {
  BaseEntity,
  BaseEntityForm,
  Response,
  entity,
  WithPermissions,
  WithTenant,
} from "@core/model";
import {
  PermissionsDialog,
  PermissionsDialogProps,
} from "./permissions/PermissionsDialog";
import {Paginator} from "./Paginator";
import {api} from "@core/api";
import {useDispatch} from "react-redux";
import {AddIcon, RefreshIcon} from "@components/icons";
import {
  UseAddEntity,
  UseDeleteEntityById,
  UseFindEntity,
  UseFindEntityById,
  UseUpdateEntity,
} from "@core/api/crud";
import {useDownBreakpoint} from "@hooks";
import {sort} from "@hooks/useSearchParams";

type size = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export type CardCrudProps<T extends BaseEntity> = {
  title: string;
  entity: entity;
  parentId?: string;
  cardSize: {[key in Breakpoint]: size} | size;
  CardItem: React.ComponentType<CrudCardItemProps<T>>;
  EntityIcon: React.ComponentType<SvgIconOwnProps>;
  form: BaseEntityForm;
  newEntityData?: Partial<T>;
  formLabel?: Partial<FormLabel>;
  newDialogLabel?: Partial<ConfirmDialogLabel>;
  editDialogLabel?: Partial<ConfirmDialogLabel>;
  deleteDialogLabel?: Partial<ConfirmDialogLabel>;
  selectValues?: {[key: string]: {id: string | number; name: string}[]};
  searchParams?: URLSearchParams;
  hideTemplateButton?: boolean;
  refetchTS?: number;
  ignoreTenant?: boolean;
  customControls?: React.ReactNode;
  sx?: SxProps<Theme>;
  showPath?: boolean;
  sort?: {
    value: sort;
    onSort: (sort: string) => void;
  };
  useFindEntityById: UseFindEntityById<T>;
  useFindEntity: UseFindEntity<T>;
  useAddEntity: UseAddEntity<T>;
  useUpdateEntity: UseUpdateEntity<T>;
  useDeleteEntityById: UseDeleteEntityById;
  onLoading?: (loading: boolean) => void;
  onItemClick?: (entity: T) => void;
  onEntityEdit?: (entity: T) => void;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  onFieldChange?: (field: string, value: any) => void;
  onEditClose?: () => void;
  onSearch?: (text: string) => void;
  onClear?: () => void;
};

export type CrudCardItemProps<T extends BaseEntity> = {
  index: number;
  entity: T;
  sx?: SxProps<Theme>;
  readonly?: boolean;
  minimized?: boolean;
  elevation?: number;
  showPath?: boolean;
  onClick?: (e: T) => void;
  onEditClick?: (e: T) => void;
  onDeleteClick?: (id: string | number) => void;
  onPermissionsClick?: (e: T) => void;
};

const CardLoadingItem = () => (
  <Card>
    <Box>
      <Skeleton animation="wave" variant="rectangular" height={80} />
      <Box p={2}>
        <Typography variant="h5">
          <Skeleton animation="wave" />
        </Typography>
        <Typography variant="body2" mt={1}>
          <Skeleton animation="wave" />
        </Typography>
        <Typography variant="body2" width="80%">
          <Skeleton animation="wave" />
        </Typography>
      </Box>
    </Box>
  </Card>
);

export type DialogState<T extends BaseEntity> = (
  | DeleteDialogProps<T>
  | EditDialogProps<T>
  | PermissionsDialogProps<T>
) & {action: "add" | "edit" | "delete" | "permissions" | undefined};

function CardCrud<T extends BaseEntity | WithTenant | WithPermissions>(
  props: CardCrudProps<T>,
) {
  const {
    entity,
    parentId,
    newEntityData,
    form,
    formLabel,
    editDialogLabel,
    deleteDialogLabel,
    selectValues,
    searchParams,
    EntityIcon,
    CardItem,
    refetchTS,
    customControls,
    sx,
    showPath,
    cardSize: cardSizeProp,
    sort: sortProp,
    onSearch,
    onClear,
    onLoading,
    useFindEntity,
    useAddEntity,
    useUpdateEntity,
    useDeleteEntityById,
    onItemClick,
    onEntityEdit,
    onPageChange,
    onRowsPerPageChange,
    onFieldChange,
    onEditClose,
  } = props;

  const cardSize = cardSizeProp ?? {
    xl: 4,
    lg: 4,
    md: 4,
    sm: 4,
    xs: 12,
  };

  const initDialogState: DialogState<T> = {
    open: false,
    form,
    data: {},
    isLoading: false,
    onConfirm: () => {},
    action: undefined,
    entity,
  };

  const dispatch = useDispatch();

  const {
    data,
    refetch,
    isFetching,
    isLoading: isFindLoading,
  } = useFindEntity(`${searchParams ? searchParams?.toString() : ""}`);
  const [deleteEntityById, {isLoading: isDeleteLoading}] =
    useDeleteEntityById();
  const [addEntity, {isLoading: isAddLoading}] = useAddEntity();
  const [updateEntity, {isLoading: isUpdateLoading}] = useUpdateEntity();
  const isLoading =
    isFetching ||
    isFindLoading ||
    isDeleteLoading ||
    isUpdateLoading ||
    isAddLoading;

  const [dialog, setDialog] = useState<DialogState<T>>(initDialogState);
  const isDownSm = useDownBreakpoint("sm");

  useEffect(() => {
    setDialog(d => {
      return {...d, loading: isLoading};
    });
    if (onLoading) {
      onLoading(isLoading);
    }
  }, [isLoading]);

  useEffect(() => {
    if (refetchTS) {
      refetch();
    }
  }, [refetchTS]);

  useEffect(() => {
    setDialog(old => {
      const newDialog = {...old};
      newDialog.data = {
        ...newDialog.data,
        ...newEntityData,
      } as Partial<T>;
      return newDialog;
    });
  }, [newEntityData]);

  const handleCloseModal = (d: DialogState<T>) => {
    setDialog({...d, data: {}, open: false});
    if (onEditClose) {
      onEditClose();
    }
  };

  const handleEdit = (response: Response<BaseEntity | unknown> | null) => {
    handleCloseModal(dialog!);
    const id = (response?.data as BaseEntity)?.id;
    if (id) {
      setTimeout(() => {
        dispatch(api.util.invalidateTags([{type: "file", id}]));
      }, 1000);
    }
  };

  const handleConfirm = (action: "add" | "edit" | "delete", e: Partial<T>) => {
    if (action === "delete") {
      deleteEntityById(e.id!).unwrap().then(handleEdit);
      handleCloseModal(dialog!);
    } else if (action === "edit" && e.id) {
      updateEntity(e).unwrap().then(handleEdit);
    } else {
      addEntity(e).unwrap().then(handleEdit);
    }
  };

  const handleItemEditClick = (
    action: "add" | "edit",
    entity: Partial<T> | undefined,
  ) => {
    if (action === "edit" && entity && onEntityEdit) {
      onEntityEdit(entity as T);
    }
    const d: DialogState<T> = {
      open: true,
      label: editDialogLabel,
      action: action,
      data: entity ?? {},
      isLoading: false,
      form,
      formLabel,
      selectValues,
      EntityIcon,
      onConfirm: (e: Partial<T>) => handleConfirm(action, e),
      onClose: () => handleCloseModal(d),
    };
    setDialog(d);
  };

  const handleItemDeleteClick = (entity: T) => {
    const d: DialogState<T> = {
      open: true,
      data: entity,
      label: {
        ...deleteDialogLabel,
        title: `${translate("deleteDialog.title")} ${entity.name}`,
      },
      action: "delete",
      isLoading: false,
      EntityIcon,
      onConfirm: (e: Partial<T>) => handleConfirm("delete", e),
      onClose: () => handleCloseModal(d),
    };
    setDialog(d);
  };

  const handlePermissionsClick = (data: T) => {
    const d: DialogState<T> = {
      action: "permissions",
      open: true,
      data,
      entity,
      isLoading: false,
      onConfirm: (e: Partial<T>) => handleConfirm("edit", e),
      onClose: () => handleCloseModal(d),
    };
    setDialog(d);
  };

  const cardContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Stack
      flexDirection={"column"}
      flex={"1 1 0"}
      position={"relative"}
      sx={{...sx}}>
      <Stack
        className="crud-controls"
        direction="row"
        pt={isDownSm ? 0 : 1}
        pb={isDownSm ? 1 : 2}
        gap={2}>
        {onSearch && onClear && (
          <InputSearch
            entity={entity}
            onSearch={onSearch}
            onClear={onClear}
            sort={sortProp}
          />
        )}
        {customControls}
        <Button
          sx={{
            minWidth: 40,
            maxWidth: 40,
            height: 40,
            p: 1,
            borderRadius: 8,
            ml: "auto",
            "& .MuiButton-icon": {m: 0},
          }}
          size="medium"
          variant="text"
          onClick={() => refetch()}
          loading={isLoading}>
          <RefreshIcon color="action" fontSize="medium" />
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon fontSize="inherit" />}
          onClick={() => handleItemEditClick("add", newEntityData)}>
          {formLabel?.new ?? translate("common.action.new")}
        </Button>
      </Stack>

      <Stack flex="1 1 0" overflow="auto">
        <Grid container ref={cardContainerRef} overflow="auto" spacing={2}>
          {isLoading && (
            <>
              <Grid size={cardSize}>
                <CardLoadingItem />
              </Grid>
              <Grid size={cardSize}>
                <CardLoadingItem />
              </Grid>
              <Grid size={cardSize}>
                <CardLoadingItem />
              </Grid>
              <Grid size={cardSize}>
                <CardLoadingItem />
              </Grid>
            </>
          )}

          {!isLoading &&
            data?.content.map((e, i) => (
              <Grid key={e.id} size={cardSize}>
                <CardItem
                  index={i}
                  entity={e}
                  onClick={onItemClick}
                  onEditClick={() => handleItemEditClick("edit", e)}
                  onDeleteClick={() => handleItemDeleteClick(e)}
                  onPermissionsClick={() => handlePermissionsClick(e)}
                  showPath={showPath}
                />
              </Grid>
            ))}
        </Grid>
        {!isLoading && data?.totalElements === 0 && (
          <EmptyData
            Icon={EntityIcon}
            sx={{flex: "1 1 0", mt: 4}}
            message={translate("common.table.noRowsLabel")}
          />
        )}
      </Stack>

      {data && onPageChange && onRowsPerPageChange && (
        <Paginator
          page={data.number}
          rowsPerPage={data.size}
          count={data.totalElements}
          onPageChange={(_e, page) => onPageChange(page)}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      {dialog?.action === "delete" && (
        <DeleteDialog {...(dialog as DeleteDialogProps<T>)} />
      )}
      {(dialog?.action === "add" || dialog?.action === "edit") && (
        <EditDialog
          {...(dialog as EditDialogProps<T>)}
          form={form}
          entity={entity}
          parentId={parentId}
          onFieldChange={onFieldChange}
        />
      )}
      {dialog.action === "permissions" && (
        <PermissionsDialog {...(dialog as PermissionsDialogProps<T>)} />
      )}
    </Stack>
  );
}

export {CardCrud};
